<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useEmitter } from 'dashboard/composables/emitter';
import LabelManager from './LabelManager.vue';
import { channelImageFor } from 'dashboard/helper/channelIcon';
import { OMNI_VIEWS } from 'dashboard/store/modules/omniInbox';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const activeView = useMapGetter('omniInbox/getActiveView');
const activePriority = useMapGetter('omniInbox/getPriority');
const inboxes = useMapGetter('inboxes/getInboxes');
// Sidebar list = only labels marked "show on sidebar" (incl. the user's private ones).
const labels = useMapGetter('labels/getLabelsOnSidebarWithPrivate');

// Per-folder conversation counts (computed server-side, scoped to the current user).
const counts = useMapGetter('omniInbox/getCounts');
const viewCount = key => counts.value?.views?.[key] ?? 0;
const priorityCount = priority => counts.value?.priorities?.[priority] ?? 0;
const labelCount = title => counts.value?.labels?.[title] ?? 0;
const inboxCount = id => counts.value?.inboxes?.[id] ?? 0;

const fetchCounts = () => store.dispatch('omniInbox/fetchCounts');
onMounted(fetchCounts);
// Refresh counts whenever conversation stats are refreshed (list changes).
useEmitter('fetch_conversation_stats', fetchCounts);

const accountId = computed(() => route.params.accountId);

const VIEW_ITEMS = [
  { key: OMNI_VIEWS.MY_INBOX, icon: 'i-lucide-inbox' },
  { key: OMNI_VIEWS.UNASSIGNED, icon: 'i-lucide-circle-dashed' },
  { key: OMNI_VIEWS.ALL, icon: 'i-lucide-layers' },
  { key: OMNI_VIEWS.AWAITING_MY_REPLY, icon: 'i-lucide-reply' },
  { key: OMNI_VIEWS.AWAITING_THEIR_REPLY, icon: 'i-lucide-clock' },
  { key: OMNI_VIEWS.NO_ACTION_NEEDED, icon: 'i-lucide-circle-slash' },
  { key: OMNI_VIEWS.DONE, icon: 'i-lucide-check-check' },
];

const PRIORITIES = ['urgent', 'high', 'medium', 'low'];

const PRIORITY_COLORS = {
  urgent: 'text-n-ruby-9',
  high: 'text-n-amber-9',
  medium: 'text-n-blue-9',
  low: 'text-n-slate-9',
};

function channelIcon(inbox) {
  return channelImageFor(inbox.channel_type, inbox.medium);
}

const activeInboxId = computed(() => Number(route.params.inbox_id) || null);
const activeLabel = computed(() => route.params.label || null);
// A view folder is the active one only when no priority folder / inbox / label is active.
const isViewActive = key =>
  activeView.value === key &&
  !activePriority.value &&
  !activeInboxId.value &&
  !activeLabel.value;

const goHome = () => {
  if (route.name !== 'home') {
    router.push({ name: 'home', params: { accountId: accountId.value } });
  }
};

function selectView(key) {
  store.dispatch('omniInbox/setActiveView', key);
  // Top-level folders live on the home route — clear any inbox/label context.
  goHome();
}

function selectInbox(inbox) {
  store.dispatch('omniInbox/setPriority', null);
  router.push({
    name: 'inbox_dashboard',
    params: { accountId: accountId.value, inbox_id: inbox.id },
  });
}

function selectLabel(label) {
  store.dispatch('omniInbox/setPriority', null);
  router.push({
    name: 'label_conversations',
    params: { accountId: accountId.value, label: label.title },
  });
}

// Priority folders behave like views: select one (no toggle/clear button).
function selectPriority(priority) {
  store.dispatch('omniInbox/setPriority', priority);
  goHome();
}

// Collapsible sections (Inbox Channels / Priority / Labels).
const collapsed = ref({ channels: false, priority: false, labels: false });
function toggleSection(key) {
  collapsed.value = { ...collapsed.value, [key]: !collapsed.value[key] };
}

// Label management / create popup (opened from the Labels "+").
const showLabelManager = ref(false);

function openLabelManager() {
  showLabelManager.value = true;
}
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 w-[220px] h-full overflow-hidden bg-n-surface-1 rounded-2xl"
  >
    <h2 class="px-4 pt-4 pb-2 text-base font-medium text-n-slate-12">
      {{ t('CHAT_LIST.OMNI_INBOX.TITLE') }}
    </h2>

    <nav class="flex-1 overflow-y-auto px-2 pb-4">
      <!-- Views -->
      <ul class="flex flex-col gap-0.5 m-0 list-none">
        <li v-for="item in VIEW_ITEMS" :key="item.key">
          <button
            class="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-sm text-left text-n-slate-12 hover:bg-n-alpha-2"
            :class="{ 'bg-n-alpha-2 font-medium': isViewActive(item.key) }"
            @click="selectView(item.key)"
          >
            <span :class="item.icon" class="flex-shrink-0 size-4 text-n-slate-11" />
            <span class="flex-1 truncate">
              {{ t(`CHAT_LIST.OMNI_INBOX.VIEWS.${item.key}`) }}
            </span>
            <span class="text-xs shrink-0 text-n-slate-10">
              {{ viewCount(item.key) }}
            </span>
          </button>
        </li>
      </ul>

      <!-- Inbox Channels -->
      <div v-if="inboxes.length" class="mt-4">
        <button
          class="flex items-center justify-between w-full px-2 mb-1 text-xs font-medium tracking-wide uppercase text-n-slate-10 hover:text-n-slate-12"
          @click="toggleSection('channels')"
        >
          <span>{{ t('CHAT_LIST.OMNI_INBOX.INBOX_CHANNELS') }}</span>
          <span
            class="size-4"
            :class="
              collapsed.channels ? 'i-lucide-chevron-down' : 'i-lucide-chevron-up'
            "
          />
        </button>
        <ul v-show="!collapsed.channels" class="flex flex-col gap-0.5 m-0 list-none">
          <li v-for="inbox in inboxes" :key="inbox.id">
            <button
              class="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-sm text-left text-n-slate-12 hover:bg-n-alpha-2"
              :class="{ 'bg-n-alpha-2 font-medium': activeInboxId === inbox.id }"
              @click="selectInbox(inbox)"
            >
              <img
                :src="channelIcon(inbox)"
                :alt="inbox.channel_type"
                class="flex-shrink-0 rounded-full size-5"
              />
              <span class="flex-1 truncate">{{ inbox.name }}</span>
              <span class="text-xs shrink-0 text-n-slate-10">
                {{ inboxCount(inbox.id) }}
              </span>
            </button>
          </li>
        </ul>
      </div>

      <!-- Priority -->
      <div class="mt-4">
        <button
          class="flex items-center justify-between w-full px-2 mb-1 text-xs font-medium tracking-wide uppercase text-n-slate-10 hover:text-n-slate-12"
          @click="toggleSection('priority')"
        >
          <span>{{ t('CHAT_LIST.OMNI_INBOX.PRIORITY.TITLE') }}</span>
          <span
            class="size-4"
            :class="
              collapsed.priority ? 'i-lucide-chevron-down' : 'i-lucide-chevron-up'
            "
          />
        </button>
        <ul v-show="!collapsed.priority" class="flex flex-col gap-0.5 m-0 list-none">
          <li v-for="priority in PRIORITIES" :key="priority">
            <button
              class="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-sm text-left text-n-slate-12 hover:bg-n-alpha-2"
              :class="{ 'bg-n-alpha-2 font-medium': activePriority === priority }"
              @click="selectPriority(priority)"
            >
              <span
                class="i-lucide-signal flex-shrink-0 size-4"
                :class="PRIORITY_COLORS[priority]"
              />
              <span class="flex-1 truncate">
                {{ t(`CHAT_LIST.OMNI_INBOX.PRIORITY.${priority}`) }}
              </span>
              <span class="text-xs shrink-0 text-n-slate-10">
                {{ priorityCount(priority) }}
              </span>
            </button>
          </li>
        </ul>
      </div>

      <!-- Labels -->
      <div class="mt-4">
        <div class="flex items-center justify-between px-2 mb-1 text-n-slate-10">
          <button
            class="flex-1 text-xs font-medium tracking-wide text-left uppercase hover:text-n-slate-12"
            @click="toggleSection('labels')"
          >
            {{ t('CHAT_LIST.OMNI_INBOX.LABELS.TITLE') }}
          </button>
          <div class="flex items-center gap-1.5">
            <button
              v-tooltip.left="t('CHAT_LIST.OMNI_INBOX.LABELS.ADD')"
              class="i-lucide-plus size-4 hover:text-n-slate-12"
              @click.stop="openLabelManager"
            />
            <button
              class="size-4 hover:text-n-slate-12"
              :class="
                collapsed.labels ? 'i-lucide-chevron-down' : 'i-lucide-chevron-up'
              "
              @click="toggleSection('labels')"
            />
          </div>
        </div>
        <ul v-show="!collapsed.labels" class="flex flex-col gap-0.5 m-0 list-none">
          <li v-for="label in labels" :key="label.id">
            <button
              class="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-sm text-left text-n-slate-12 hover:bg-n-alpha-2"
              :class="{ 'bg-n-alpha-2 font-medium': activeLabel === label.title }"
              @click="selectLabel(label)"
            >
              <span
                class="flex-shrink-0 rounded-full size-3.5"
                :style="{ backgroundColor: label.color }"
              />
              <span class="flex-1 truncate">{{ label.title }}</span>
              <span class="text-xs shrink-0 text-n-slate-10">
                {{ labelCount(label.title) }}
              </span>
            </button>
          </li>
        </ul>
      </div>
    </nav>

    <LabelManager v-model:show="showLabelManager" />
  </div>
</template>
